import CardAIStream from "@/components/card-ai-stream";
import { FadeInSection } from "@/components/company/company-animations";
import CardCompanyStock from "@/components/company/card-company-stock";
import CardFinancialRatios from "@/components/company/card-financial-ratios";
import CardFinancialsSummary from "@/components/company/card-financials-summary";
import CardCompanyFunding from "@/components/company/card-company-funding";
import CardFinancialsHistory from "@/components/company/card-financials-history";
import { FinancialMetricGrid } from "@/components/company/financial-metric-tile";
import CompanySectionCard, {
  CompanyCardSkeleton,
} from "@/components/company/company-section-card";
import CompanyTabReadFailed from "@/components/company/company-tab-read-failed";
import {
  useAreSectionsFailed,
  useAreSectionsPending,
} from "@/contexts/company-sections-context";
import { useTranslation } from "@/hooks/useTranslation";
import {
  COMPANY_CARD_SECTIONS,
  COMPANY_TAB_SECTIONS,
} from "@/lib/company-card-sections";
import {
  hasFinancialRatiosData,
  hasFinancialsHistoryData,
  hasFinancialsSummaryData,
  hasFundingData,
  hasStockData,
} from "@/lib/company-data-availability";
import { type ForecastData, parseForecast } from "@/lib/parse-forecast";
import { useCallback, useState } from "react";
import { useCompanyTabContext } from "./company-tab-context";

export default function CompanyFinancialsPage() {
  const { company, done } = useCompanyTabContext();
  const { t } = useTranslation();
  const [forecastData, setForecastData] = useState<ForecastData | null>(null);
  const hasForecastStream = company.business.finances.length >= 2;
  const [isForecastLoading, setIsForecastLoading] = useState(hasForecastStream);
  const isPending = useAreSectionsPending(COMPANY_TAB_SECTIONS.financials);
  const isFailed = useAreSectionsFailed(COMPANY_TAB_SECTIONS.financials);
  // Every card hides on a failed section: the tab says nothing was read.
  const isReadFailed =
    isFailed &&
    !isPending &&
    !hasFinancialsSummaryData(company) &&
    !hasFinancialRatiosData(company) &&
    !hasFinancialsHistoryData(company) &&
    !hasStockData(company) &&
    !hasFundingData(company);

  const handleTextChange = useCallback(
    (text: string, isComplete: boolean) => {
      if (isComplete) {
        setForecastData(parseForecast(text));
        setIsForecastLoading(false);
      } else {
        setForecastData(null);
        setIsForecastLoading(true);
      }
    },
    [],
  );

  return (
    <FadeInSection>
    <div className="flex flex-col gap-4 @container sm:gap-5">
      {isReadFailed && <CompanyTabReadFailed />}
      {/* One grid: the ratios fill the summary's last row. */}
      <CompanySectionCard
        sections={[
          ...COMPANY_CARD_SECTIONS.financialsSummary,
          ...COMPANY_CARD_SECTIONS.financialRatios,
        ]}
        hasData={
          hasFinancialsSummaryData(company) || hasFinancialRatiosData(company)
        }
        skeleton={<CompanyCardSkeleton />}
      >
        <FinancialMetricGrid>
          <CardFinancialsSummary company={company} />
          <CardFinancialRatios company={company} />
        </FinancialMetricGrid>
      </CompanySectionCard>
      <CompanySectionCard
        sections={COMPANY_CARD_SECTIONS.financialsHistory}
        hasData={hasFinancialsHistoryData(company)}
        skeleton={<CompanyCardSkeleton className="min-h-[240px]" />}
      >
        <CardFinancialsHistory
          company={company}
          forecastData={forecastData}
          isForecastLoading={hasForecastStream && isForecastLoading}
        />
      </CompanySectionCard>
      <CompanySectionCard
        sections={COMPANY_CARD_SECTIONS.stock}
        hasData={hasStockData(company)}
        skeleton={<CompanyCardSkeleton />}
      >
        <CardCompanyStock company={company} />
      </CompanySectionCard>
      <CompanySectionCard
        sections={COMPANY_CARD_SECTIONS.funding}
        hasData={hasFundingData(company)}
        skeleton={<CompanyCardSkeleton />}
      >
        <CardCompanyFunding company={company} standalone />
      </CompanySectionCard>
      {/* The forecast reads the whole sheet server-side: it waits for the run. */}
      {hasForecastStream && (
        <CardAIStream
          title={t("company.financials.forecast.title")}
          streamUrl={`/company/${company.id}/financial/forecast`}
          launchFetch={done}
          onTextChange={handleTextChange}
        />
      )}
    </div>
    </FadeInSection>
  );
}
